import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { trackButtonClick } from '../../utils/analytics';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';

const LandingHeader: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  // @ts-ignore
  const { t } = useTranslation('landing');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        isMenuOpen &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);

  const toggleMenu = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuClick = (e: React.MouseEvent | React.TouchEvent) => {
    // e.preventDefault();
    // e.stopPropagation();
  };

  return (
    <>
      <header
        className="sticky top-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10"
        style={{
          paddingTop: 'env(safe-area-inset-top)'
        }}
      >
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div
              className="flex items-center cursor-pointer"
              onClick={() => {
                navigate(`/${lang}/home`);
                // Sayfanın başına scroll yap
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <img
                src="/logo.svg"
                alt="Winnomi Logo"
                className="w-14 h-14 md:w-16 md:h-16 object-contain"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <a href={`/${lang}/home#features`} className="text-white/80 hover:text-white transition-colors">
                {t('header.features')}
              </a>
              <a href={`/${lang}/home#pricing`} className="text-white/80 hover:text-white transition-colors">
                {t('header.pricing')}
              </a>
              <button
                onClick={() => navigate(`/${lang}/blog`)}
                className="text-white/80 hover:text-white transition-colors"
              >
                {t('header.blog')}
              </button>
              <button
                onClick={() => navigate(`/${lang}/hizmetler`)}
                className="text-white/80 hover:text-white transition-colors"
              >
                {t('header.services')}
              </button>
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center space-x-4">
              <LanguageSwitcher />
              <button
                onClick={() => {
                  trackButtonClick('login', 'header_desktop');
                  navigate(`/${lang}/login`);
                }}
                className="text-white/80 hover:text-white transition-colors"
              >
                {t('header.login')}
              </button>
              <button
                onClick={() => {
                  trackButtonClick('register', 'header_desktop');
                  navigate(`/${lang}/register`);
                }}
                className="bg-gradient-to-r from-purple-500 to-purple-700 px-6 py-2 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 transform hover:scale-105"
              >
                {t('header.register')}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              ref={buttonRef}
              onClick={toggleMenu}
              className="lg:hidden flex items-center justify-center w-12 h-12 text-white/80 hover:text-white transition-colors touch-manipulation rounded-lg hover:bg-white/10 active:bg-white/20"
              style={{
                minHeight: '48px',
                minWidth: '48px',
                WebkitTapHighlightColor: 'transparent',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none'
              }}
            >
              <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} className="text-xl pointer-events-none" />
            </button>
          </div>
        </div>
        {/* Mobile Menu */}
        <div
          ref={menuRef}
          className={`lg:hidden fixed top-full left-0 right-0 bg-gray-900/95 backdrop-blur-xl border-b border-white/10 z-[60] shadow-2xl transition-all duration-300 ease-out ${isMenuOpen
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 -translate-y-4 pointer-events-none'
            }`}
          onClick={handleMenuClick}
          onTouchStart={handleMenuClick}
          style={{
            WebkitOverflowScrolling: 'touch',
            WebkitTransform: 'translateZ(0)',
            transform: 'translateZ(0)'
          }}
        >
          <div className="px-6 py-4 space-y-2">
            <a
              className="block py-3 px-2 text-white/80 hover:text-white transition-colors touch-manipulation active:bg-white/10"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/${lang}/home#features`);
                setIsMenuOpen(false);
                setTimeout(() => {
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                }, 200);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/${lang}/home#features`);
                setIsMenuOpen(false);
                setTimeout(() => {
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                }, 200);
              }}
              style={{
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                WebkitTapHighlightColor: 'transparent',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                cursor: 'pointer'
              }}
            >
              {t('header.features')}
            </a>
            <a
              className="block py-3 px-2 text-white/80 hover:text-white transition-colors touch-manipulation active:bg-white/10"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/${lang}/home#pricing`);
                setIsMenuOpen(false);
                setTimeout(() => {
                  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                }, 200);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/${lang}/home`);
                setIsMenuOpen(false);
                setTimeout(() => {
                  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                }, 200);
              }}
              style={{
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                WebkitTapHighlightColor: 'transparent',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                cursor: 'pointer'
              }}
            >
              {t('header.pricing')}
            </a>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/${lang}/blog`);
                setIsMenuOpen(false);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/${lang}/blog`);
                setIsMenuOpen(false);
              }}
              className="block w-full text-left py-3 px-2 text-white/80 hover:text-white transition-colors touch-manipulation active:bg-white/10"
              style={{
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                WebkitTapHighlightColor: 'transparent',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                cursor: 'pointer'
              }}
            >
              {t('header.blog')}
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/${lang}/hizmetler`);
                setIsMenuOpen(false);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/${lang}/hizmetler`);
                setIsMenuOpen(false);
              }}
              className="block w-full text-left py-3 px-2 text-white/80 hover:text-white transition-colors touch-manipulation active:bg-white/10"
              style={{
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                WebkitTapHighlightColor: 'transparent',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                cursor: 'pointer'
              }}
            >
              {t('header.services')}
            </button>
            <div className="pt-4 border-t border-white/10 space-y-2">
              <LanguageSwitcher />
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  trackButtonClick('login', 'header_mobile');
                  navigate(`/${lang}/login`);
                  setIsMenuOpen(false);
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  trackButtonClick('login', 'header_mobile');
                  navigate(`/${lang}/login`);
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left py-3 px-2 text-white/80 hover:text-white transition-colors touch-manipulation active:bg-white/10"
                style={{
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  WebkitTapHighlightColor: 'transparent',
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none',
                  cursor: 'pointer'
                }}
              >
                {t('header.login')}
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  trackButtonClick('register', 'header_mobile');
                  navigate(`/${lang}/register`);
                  setIsMenuOpen(false);
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  trackButtonClick('register', 'header_mobile');
                  navigate(`/${lang}/register`);
                  setIsMenuOpen(false);
                }}
                className="w-full bg-gradient-to-r from-purple-500 to-purple-700 px-6 py-4 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 touch-manipulation active:from-purple-600 active:to-purple-800"
                style={{
                  minHeight: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  WebkitTapHighlightColor: 'transparent',
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none',
                  cursor: 'pointer'
                }}
              >
                {t('header.register')}
              </button>
            </div>
          </div>
        </div>
      </header>
      <div
        className={`fixed inset-0 z-40 h-[100vh] mt-[65px] bg-gray-900/25 backdrop-blur-xl lg:hidden transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        onClick={() => setIsMenuOpen(false)}
      />
    </>
  );
};

export default LandingHeader;
